import { store } from "@app/store"
import { selectAccessToken } from "@features/login/loginSelectors"
import {
  Configuration,
  CreateConsultantDto,
  ConsultantApi,
  ConsultantDto,
  TeamLeadConsultants,
  UpdateConsultantProgramExpertiseRequestProgramTypeEnum,
  UpdateConsultantProgramExpertiseRequestProgramDomainEnum,
} from "@masterschool/course-builder-api"
export class ConsultantClient {
  private static consultantClient = new ConsultantApi(
    new Configuration({
      basePath: `${import.meta.env.VITE_BASE_URL ?? ""}/api`,
    }),
  )

  private static makeHeaders() {
    return {
      headers: {
        Authorization: `Bearer ${selectAccessToken(store.getState())}`,
      },
    }
  }

  static getSalesTeams(): Promise<TeamLeadConsultants[]> {
    return ConsultantClient.consultantClient
      .consultantControllerListSalesTeams(this.makeHeaders())
      .then((response) => response.data)
  }

  // Tag catalog operations. Hand-written fetch since these routes are newer
  // than the generated client.
  private static salesBase() {
    return `${import.meta.env.VITE_BASE_URL ?? ""}/api/v1/sales`
  }

  private static jsonHeaders() {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${selectAccessToken(store.getState())}`,
    }
  }

  static async listTagCounts(): Promise<
    { tag: string; consultantCount: number }[]
  > {
    const response = await fetch(`${this.salesBase()}/tags`, {
      headers: this.jsonHeaders(),
    })
    if (!response.ok) {
      throw new Error("Failed to load tags")
    }
    return response.json()
  }

  static async createTag(name: string): Promise<void> {
    const response = await fetch(`${this.salesBase()}/create-tag`, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ name }),
    })
    if (!response.ok) {
      throw new Error("Failed to create tag")
    }
  }

  static async renameTag(from: string, to: string): Promise<void> {
    const response = await fetch(`${this.salesBase()}/rename-tag`, {
      method: "PUT",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ from, to }),
    })
    if (!response.ok) {
      throw new Error("Failed to rename tag")
    }
  }

  static async deleteTag(tag: string): Promise<void> {
    const response = await fetch(`${this.salesBase()}/delete-tag`, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ tag }),
    })
    if (!response.ok) {
      throw new Error("Failed to delete tag")
    }
  }

  static create(consultantDto: CreateConsultantDto): Promise<ConsultantDto> {
    return ConsultantClient.consultantClient
      .consultantControllerCreate(consultantDto, this.makeHeaders())
      .then((response) => response.data)
  }

  static updateConsultantAvailability(
    consultantId: string,
    isAvailable: boolean,
  ): Promise<ConsultantDto> {
    return ConsultantClient.consultantClient
      .consultantControllerUpdateConsultantAvailability(
        { consultantId, isAvailable },
        this.makeHeaders(),
      )
      .then((response) => response.data)
  }

  // Hand-written: the generated ConsultantApi predates this endpoint, so call
  // it directly with the same base + bearer token.
  static async updateConsultantTags(
    consultantId: string,
    tags: string[],
  ): Promise<ConsultantDto> {
    const response = await fetch(
      `${
        import.meta.env.VITE_BASE_URL ?? ""
      }/api/v1/sales/update-consultant-tags`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${selectAccessToken(store.getState())}`,
        },
        body: JSON.stringify({ consultantId, tags }),
      },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to update consultant tags (${response.status} ${response.statusText})`,
      )
    }
    return response.json()
  }

  static async updateConsultantTeamLead(
    consultantId: string,
    teamLeadUserId: string,
  ): Promise<void> {
    const response = await fetch(
      `${
        import.meta.env.VITE_BASE_URL ?? ""
      }/api/v1/sales/update-consultant-team-lead`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${selectAccessToken(store.getState())}`,
        },
        body: JSON.stringify({ consultantId, teamLeadUserId }),
      },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to reassign consultant team lead (${response.status} ${response.statusText})`,
      )
    }
  }

  static async listTeamLeadCandidates(): Promise<
    { userId: string; name: string; email: string }[]
  > {
    const response = await fetch(
      `${
        import.meta.env.VITE_BASE_URL ?? ""
      }/api/v1/sales/team-lead-candidates`,
      {
        headers: {
          Authorization: `Bearer ${selectAccessToken(store.getState())}`,
        },
      },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to load team lead candidates (${response.status})`,
      )
    }
    return response.json()
  }

  static async changeTeamLead(
    fromUserId: string,
    toUserId: string,
  ): Promise<void> {
    const response = await fetch(
      `${import.meta.env.VITE_BASE_URL ?? ""}/api/v1/sales/change-team-lead`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${selectAccessToken(store.getState())}`,
        },
        body: JSON.stringify({ fromUserId, toUserId }),
      },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to change team lead (${response.status} ${response.statusText})`,
      )
    }
  }

  static async renameTeam(teamLeadUserId: string, name: string): Promise<void> {
    const response = await fetch(
      `${import.meta.env.VITE_BASE_URL ?? ""}/api/v1/sales/rename-team`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${selectAccessToken(store.getState())}`,
        },
        body: JSON.stringify({ teamLeadUserId, name }),
      },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to rename team (${response.status} ${response.statusText})`,
      )
    }
  }

  // Every consultant, flat (not grouped by team) — routing pickers need the
  // full pool, including consultants not currently in a team.
  static async listAllConsultants(): Promise<ConsultantDto[]> {
    const response = await fetch(
      `${import.meta.env.VITE_BASE_URL ?? ""}/api/v1/sales/all-consultants`,
      {
        headers: {
          Authorization: `Bearer ${selectAccessToken(store.getState())}`,
        },
      },
    )
    if (!response.ok) {
      throw new Error(`Failed to load consultants (${response.status})`)
    }
    return response.json()
  }

  static archiveConsultant(consultantId: string): Promise<boolean> {
    return ConsultantClient.consultantClient
      .consultantControllerArchiveConsultant(
        { consultantId },
        this.makeHeaders(),
      )
      .then((response) => response.data)
  }

  static updateConsultantProgramExpertise(
    consultantId: string,
    programType?: UpdateConsultantProgramExpertiseRequestProgramTypeEnum,
    programDomain?: UpdateConsultantProgramExpertiseRequestProgramDomainEnum,
  ): Promise<ConsultantDto> {
    return ConsultantClient.consultantClient
      .consultantControllerUpdateConsultantProgramExpertise(
        { id: consultantId, programType, programDomain },
        this.makeHeaders(),
      )
      .then((response) => response.data)
  }
}
